import { useState } from "react";
import { Alert, Stack } from "@mui/material";

import type { ClientExpoClientObjectRow, ClientTraderDefectSummary } from "../../../../lib/api/types";
import type { MasterProfile, OrderRow } from "../../../../types";
import MainContent, { type ClientHistoryViewTab } from "../../history/MainContent";
import MetricsGrid from "../../history/MetricsGrid";
import type { TransportHistoryRow } from "../../history/types";
import { useProcessedHistoryOrders } from "../../history/useProcessedHistoryOrders";
import { buildTransportHistoryFromOrders, normalizeBusinessKey, normalizeValue } from "../../history/utils";

type Props = {
    orders: OrderRow[];
    profile: MasterProfile;
    traderDefect: ClientTraderDefectSummary | null;
    transportError?: string;
    transportHistory?: TransportHistoryRow[];
    expoClientObjects?: ClientExpoClientObjectRow[];
};

const emptyTraderDefect: ClientTraderDefectSummary = {
    articlePercent: 0,
    available: false,
    defectArticles: 0,
    defectValue: 0,
    pricePercent: 0,
    purchasedArticles: 0,
    purchasedValue: 0,
};

export default function Client360HistoryTab({
    orders,
    profile,
    traderDefect,
    transportError,
    transportHistory = [],
    expoClientObjects = [],
}: Props) {
    const [activeHistoryTab, setActiveHistoryTab] = useState<ClientHistoryViewTab>("documents");
    const processedOrders = useProcessedHistoryOrders(
        orders,
        normalizeValue(profile.customer),
        normalizeBusinessKey(profile.customer),
    );
    const orderTransportHistory = buildTransportHistoryFromOrders(processedOrders.orders);
    const resolvedTransportHistory = transportHistory.length > 0
        ? transportHistory
        : orderTransportHistory;

    if (processedOrders.orders.length === 0) {
        return <Alert severity="info">Няма налична пълна история за този клиент.</Alert>;
    }

    return (
        <Stack spacing={2}>
            <MetricsGrid
                averageInterval={processedOrders.averageInterval}
                averageMonthlyFrequency={processedOrders.averageMonthlyFrequency}
                firstOrder={processedOrders.firstOrder}
                latestOrder={processedOrders.latestOrder}
                totalOrders={processedOrders.orders.length}
                totalValue={processedOrders.totalValue}
                traderDefect={traderDefect ?? emptyTraderDefect}
            />
            {transportError ? <Alert severity="info">{transportError}</Alert> : null}
            <MainContent
                activeTab={activeHistoryTab}
                intervals={processedOrders.intervals}
                onTabChange={setActiveHistoryTab}
                transportHistory={resolvedTransportHistory}
                transportHistorySource={
                    transportHistory.length > 0
                        ? "transport-db"
                        : orderTransportHistory.length > 0
                          ? "orders"
                          : "none"
                }
                expoClientObjects={expoClientObjects}
                yearlyStats={processedOrders.yearlyStats}
            />
        </Stack>
    );
}
